import { useRef, useState, type ReactNode } from 'react';
import { FileText, Undo2 } from 'lucide-react';
import type {
  InvoiceDetail, PaymentReferenceCheck, PaymentReferenceUse, PaymentSubmissionRow, PaymentSubmissionStatus,
} from '@dru/types';
import { BRAND, PAYMENT_METHOD_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, IconAction, RowActions, ConfirmDialog, DetailSection, Field, Input, Select, StatusBadge, TextField, useToast, type Tone,
} from '@dru/ui';
import { Dialog } from './Dialog';
import { api } from '../lib/api';
import { useAuth, errorMessage } from '../lib/auth';
import { formatDate, formatDateTime } from '../lib/format';
import { toBase64 } from '../lib/files';

export const SUBMISSION_LABEL: Record<PaymentSubmissionStatus, string> = {
  PENDING: 'Pending verification', CONFIRMED: 'Confirmed', REJECTED: 'Rejected', CANCELLED: 'Withdrawn',
};
/**
 * A pending submission is never drawn in a "paid" colour: until Dr Ultimate
 * has matched it to the bank statement it is a claim, not money received.
 */
export const SUBMISSION_TONE: Record<PaymentSubmissionStatus, Tone> = {
  PENDING: 'pending', CONFIRMED: 'covered', REJECTED: 'danger', CANCELLED: 'neutral',
};
const MAX_PROOF_BYTES = 1_000_000;
const PROOF_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

/**
 * Today as the browser's calendar reads it, "YYYY-MM-DD".
 *
 * Not `toISOString()`: that is the UTC date, which in India is yesterday until
 * 5:30 in the morning — and a payment made this morning could not be dated.
 */
export function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "45 KB", "1.2 MB" — the size of an attached proof. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Opens a submission's proof through its five-minute audited grant.
 *
 * The tab is opened on the click itself and pointed at the grant once it
 * arrives: a window opened after an awaited request is no longer tied to the
 * click, and popup blockers refuse it.
 */
export async function openProof(id: string, tenantId?: string) {
  const tab = window.open('about:blank', '_blank');
  try {
    const grant = await api.get<{ url: string }>(`/payment-submissions/${id}/proof${tenantId ? `?tenantId=${tenantId}` : ''}`);
    if (tab) {
      // Absolute: a blank tab has no base of its own to resolve a path
      // against. Same-origin (our own document route), so the opener is kept.
      // inline=1: shown in the tab to be read against the bank statement,
      // rather than saved as a download.
      const url = new URL(grant.url, window.location.origin);
      url.searchParams.set('inline', '1');
      tab.location.replace(url.href);
    } else {
      window.location.assign(grant.url);
    }
  } catch (e) {
    tab?.close();
    throw e;
  }
}

// ── A bank reference the account already carries ──────────────────────
//
// One UTR stood as proof on three payments and a fourth submission, and no
// screen said so (audit of 1 Oct 2026). The API now reports where else a
// reference stands; these are the pieces every screen that takes or reviews a
// payment shows it with. Reuse is a warning and never a refusal here: one
// bank transfer can pay several invoices.

const USE_STATUS: Record<PaymentReferenceUse['status'], string> = {
  PENDING: 'awaiting verification', CONFIRMED: 'confirmed as received', REJECTED: 'rejected',
  CANCELLED: 'withdrawn', RECORDED: 'recorded as received', REVERSED: 'reversed',
};

/** The uses that still stand — a claim awaiting verification, or money on the ledger. */
export const liveUses = (uses: PaymentReferenceUse[] | undefined) => (uses ?? []).filter((u) => u.live);

/** Each use on its own line: invoice, plant, amount, date and how it stands. */
export function ReferenceUseList({ uses, currency, invoiceHref }: {
  uses: PaymentReferenceUse[]; currency?: string;
  /** Where an invoice number links to, on a screen that can open it. */
  invoiceHref?: (use: PaymentReferenceUse) => string;
}) {
  return (
    <ul className="mt-1 space-y-0.5">
      {uses.map((u) => (
        <li key={`${u.kind}:${u.id}`} className="tabular-nums">
          {invoiceHref
            ? <a href={invoiceHref(u)} target="_blank" rel="noreferrer" className="font-mono font-semibold underline">{u.invoiceNumber}</a>
            : <span className="font-mono font-semibold">{u.invoiceNumber}</span>}
          {' '}· Plant {u.plantCode} · {formatPaise(u.amountPaise, currency)} · {u.kind === 'SUBMISSION' ? 'paid' : 'received'} {formatDate(u.date)}
          {' '}— {u.kind === 'SUBMISSION' ? 'submitted, ' : ''}{USE_STATUS[u.status]}
        </li>
      ))}
    </ul>
  );
}

/**
 * The warning itself. Says nothing when the reference stands nowhere else;
 * history alone (a rejected or withdrawn submission, a reversed payment) is a
 * quiet note, because resubmitting a rejected UTR is the ordinary correction.
 */
export function ReferenceReuseAlert({ reference, uses, elsewhere = 0, currency, amountPaise, invoiceHref, children, className }: {
  reference: string; uses: PaymentReferenceUse[] | undefined; elsewhere?: number; currency?: string;
  /** This payment's own amount, so the total claimed against the reference can be stated. */
  amountPaise?: number;
  invoiceHref?: (use: PaymentReferenceUse) => string;
  /** What the reader should do about it — differs for the company and for Finance. */
  children?: ReactNode;
  className?: string;
}) {
  const live = liveUses(uses);
  const past = (uses ?? []).filter((u) => !u.live);
  const count = live.length + elsewhere;
  if (count === 0 && past.length === 0) return null;
  if (count === 0) {
    return (
      <div className={`rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-caption text-neutral-600 ${className ?? ''}`}>
        Reference <span className="font-mono">{reference}</span> was used before, on a payment that no longer stands:
        <ReferenceUseList uses={past} currency={currency} invoiceHref={invoiceHref} />
      </div>
    );
  }
  const total = live.reduce((n, u) => n + u.amountPaise, 0) + (amountPaise ?? 0);
  return (
    <Alert
      tone="warning"
      className={className}
      title={<>Reference <span className="font-mono">{reference}</span> is already on {count} other payment{count === 1 ? '' : 's'}</>}
    >
      {live.length > 0 && <ReferenceUseList uses={live} currency={currency} invoiceHref={invoiceHref} />}
      {elsewhere > 0 && (
        <p className="mt-1">
          {live.length > 0 ? 'And ' : ''}{elsewhere} payment{elsewhere === 1 ? '' : 's'} at another plant of your company.
        </p>
      )}
      {amountPaise !== undefined && live.length > 0 && elsewhere === 0 && (
        <p className="mt-1">With this one, {formatPaise(total, currency)} is claimed against this single reference.</p>
      )}
      {children && <p className="mt-1">{children}</p>}
      {past.length > 0 && (
        <div className="mt-1 opacity-80">
          Earlier, and no longer standing:
          <ReferenceUseList uses={past} currency={currency} invoiceHref={invoiceHref} />
        </div>
      )}
    </Alert>
  );
}

const referenceKey = (reference: string) => reference.trim().toLowerCase();

/**
 * Asks the API where else a reference stands, before a payment is submitted
 * or recorded against `invoiceId`.
 *
 * Advisory: if the look-up fails the form carries on — the API reports the
 * same thing on the payment it accepts. The answer is kept against the
 * reference it was asked for, so editing the field drops a stale warning.
 */
export function useReferenceCheck(invoiceId: string, tenantId?: string) {
  const [answer, setAnswer] = useState<{ key: string; result: PaymentReferenceCheck } | null>(null);
  const latest = useRef(0);
  const current = (reference: string) => (answer && answer.key === referenceKey(reference) ? answer.result : null);

  async function run(reference: string): Promise<PaymentReferenceCheck | null> {
    const key = referenceKey(reference);
    if (key.length < 4) return null;
    const known = current(reference);
    if (known) return known;
    const mine = ++latest.current;
    try {
      const result = await api.get<PaymentReferenceCheck>(
        `/invoices/${invoiceId}/payment-reference-uses?reference=${encodeURIComponent(reference.trim())}${tenantId ? `&tenantId=${tenantId}` : ''}`,
      );
      // Only the latest question's answer is shown; an earlier one arriving
      // late must not put its warning under a reference since retyped.
      if (mine === latest.current) setAnswer({ key, result });
      return result;
    } catch { return null; }
  }
  return { current, run };
}

/** Whether an answer is worth stopping for: the reference stands somewhere else. */
export const referenceFlagged = (c: PaymentReferenceCheck | null) => !!c && (liveUses(c.uses).length > 0 || c.elsewhere > 0);

/**
 * Payment submissions on one invoice, from the company's side (docs/37 A1,
 * Step 6).
 *
 * A submission is a claim that the company has paid. It is listed here with
 * where it stands; it never counts as paid — only a payment Dr Ultimate has
 * confirmed does, and those are listed separately as the invoice's ledger.
 */
export function PaymentSubmissionsSection({ invoice, items, onChanged, platformTenantId }: {
  invoice: InvoiceDetail; items: PaymentSubmissionRow[]; onChanged: () => void;
  /** Set on the platform's side, which reads the company's submissions and never makes one. */
  platformTenantId?: string;
}) {
  const { permissions } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState<PaymentSubmissionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Submitting is the company's act. The platform administrator holds the
  // permission only because it holds everything it can grant.
  const canSubmit = !platformTenantId && permissions.has('invoice.payment-submit');
  const available = invoice.availableToSubmitPaise ?? 0;
  const payable = invoice.status === 'ISSUED' && (invoice.outstandingPaise ?? 0) > 0;
  const money = (p: number) => formatPaise(p, invoice.currency);

  async function withdraw(s: PaymentSubmissionRow) {
    await api.post(`/payment-submissions/${s.id}/cancel`, {}, crypto.randomUUID());
    setWithdrawing(null);
    toast('success', 'Submission withdrawn');
    onChanged();
  }

  return (
    <DetailSection
      title={`Payment submissions (${items.length})`}
      action={canSubmit && payable && available > 0
        ? <Button size="sm" onClick={() => setOpen(true)}>Submit payment</Button>
        : undefined}
    >
      {payable && canSubmit && available === 0 && (
        <p className="mb-2 text-caption text-neutral-600">
          The full outstanding amount has been submitted and is awaiting verification by {BRAND.name}.
          Nothing more can be submitted until it is confirmed or rejected.
        </p>
      )}
      {error && <Alert tone="danger" className="mb-2">{error}</Alert>}
      {items.length === 0 ? (
        <p className="text-small text-neutral-500">
          {payable && canSubmit
            ? `Paid this invoice? Submit the payment details and proof. ${BRAND.name} verifies them before the invoice counts as paid.`
            : 'No payment has been submitted against this invoice.'}
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {items.map((s) => (
            <li key={s.id} className="py-2.5 text-small">
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                <div className="min-w-0">
                  <StatusBadge tone={SUBMISSION_TONE[s.status]}>{SUBMISSION_LABEL[s.status]}</StatusBadge>
                  <div className="mt-1 text-neutral-800">
                    Paid {formatDate(s.paidOn)} · {PAYMENT_METHOD_LABEL[s.method] ?? s.method} · <span className="font-mono">{s.reference}</span>
                  </div>
                </div>
                <span className="tabular-nums font-medium text-neutral-900">{money(s.amountPaise)}</span>
              </div>
              <div className="mt-1 text-caption text-neutral-500">
                Submitted {formatDateTime(s.submittedAt)}{s.submittedByName ? ` by ${s.submittedByName}` : ''}
                {s.status !== 'PENDING' && s.reviewedAt ? ` · ${SUBMISSION_LABEL[s.status].toLowerCase()} ${formatDateTime(s.reviewedAt)}` : ''}
              </div>
              {s.status === 'PENDING' && (
                <p className="mt-1 text-caption text-neutral-600">Not counted as paid until {BRAND.name} confirms it.</p>
              )}
              {s.status === 'PENDING' && liveUses(s.referenceUses).length > 0 && (
                <ReferenceReuseAlert
                  className="mt-1.5" reference={s.reference} uses={liveUses(s.referenceUses)} currency={invoice.currency}
                >
                  {platformTenantId
                    ? 'Check that the bank credit covers every payment on this reference before confirming.'
                    : `That is fine if one transfer paid several invoices. ${BRAND.name} checks the amounts against the bank credit.`}
                </ReferenceReuseAlert>
              )}
              {s.status === 'REJECTED' && s.rejectionReason && (
                <p className="mt-1 text-caption text-danger-600">
                  Rejected: {s.rejectionReason}.{canSubmit ? ' You can submit a corrected payment.' : ''}
                </p>
              )}
              <RowActions className="mt-1.5 justify-start">
                <IconAction
                  label={`View proof ${s.proofFileName} for ${money(s.amountPaise)}`}
                  icon={FileText}
                  onClick={() => openProof(s.id, platformTenantId).catch((e) => setError(errorMessage(e)))}
                />
                {s.status === 'PENDING' && canSubmit && (
                  <IconAction label={`Withdraw the payment of ${money(s.amountPaise)}`} icon={Undo2} danger onClick={() => setWithdrawing(s)} />
                )}
              </RowActions>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <SubmitPaymentDialog
          invoice={invoice}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            toast('success', `Payment submitted. ${BRAND.name} will verify it.`);
            onChanged();
          }}
        />
      )}
      {withdrawing && (
        <ConfirmDialog
          title="Withdraw this payment submission?"
          what={`The ${money(withdrawing.amountPaise)} submission (${withdrawing.reference}) is taken out of ${BRAND.name}'s verification queue.`}
          affects={`Invoice ${invoice.invoiceNumber}. Nothing is paid or refunded by withdrawing — it only removes the claim. You can submit the payment again afterwards.`}
          confirmLabel="Withdraw submission"
          onConfirm={() => withdraw(withdrawing)}
          onCancel={() => setWithdrawing(null)}
        />
      )}
    </DetailSection>
  );
}

function SubmitPaymentDialog({ invoice, onClose, onDone }: {
  invoice: InvoiceDetail; onClose: () => void; onDone: () => void;
}) {
  const available = invoice.availableToSubmitPaise ?? 0;
  const today = localToday();
  const [rupees, setRupees] = useState((available / 100).toFixed(2));
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState<'BANK_TRANSFER' | 'UPI' | 'CHEQUE'>('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One key per dialog: a double click re-sends the same request, which the
  // API answers once.
  const idempotencyKey = useRef(crypto.randomUUID());
  const money = (p: number) => formatPaise(p, invoice.currency);

  const amountPaise = Math.round(Number(rupees) * 100);
  const amountError = !rupees.trim() || !Number.isFinite(amountPaise) || amountPaise <= 0
    ? 'Enter the amount paid.'
    : amountPaise > available ? `At most ${money(available)} can be submitted.` : null;
  const dateError = !paidOn ? 'Enter the date the payment was made.' : paidOn > today ? 'The payment date cannot be in the future.' : null;
  const referenceError = reference.trim().length < 4
    ? `Enter the ${method === 'CHEQUE' ? 'cheque number' : 'UTR or bank reference'} (at least 4 characters).`
    : null;
  const fileError = !file
    ? 'Attach the proof of payment.'
    : !PROOF_TYPES.includes(file.type) ? 'Attach a PDF, JPEG or PNG.'
      : file.size > MAX_PROOF_BYTES ? 'The proof must be 1 MB or smaller.' : null;
  const ready = !amountError && !dateError && !referenceError && !fileError;

  // A double click must send one request, not two with the same key.
  const inFlight = useRef(false);

  // Whether the reference already stands elsewhere on the company's account.
  // Asked when the field is left, and again on submit if it was not; shown
  // before anything is sent. A reference on a payment Dr Ultimate recorded
  // is a warning the company can submit past — one transfer may cover several
  // invoices. One already on a live submission is the API's own refusal
  // (one live submission per reference), said here rather than after the click.
  const refCheck = useReferenceCheck(invoice.id);
  const seen = referenceError ? null : refCheck.current(reference);
  const duplicateSubmission = liveUses(seen?.uses).find((u) => u.kind === 'SUBMISSION');
  const flagged = referenceFlagged(seen);

  async function submit() {
    // Errors are shown once somebody has tried to submit, not while they are
    // still on the first field of an empty form.
    setTried(true);
    if (!ready || !file || inFlight.current || duplicateSubmission) return;
    inFlight.current = true;
    setBusy(true); setError(null);
    try {
      // The warning has to have been on screen before this click counts as
      // "submit anyway". If the look-up had not run, it runs now, and a
      // flagged reference stops here for the company to read.
      if (!seen && referenceFlagged(await refCheck.run(reference))) return;
      await api.post(`/invoices/${invoice.id}/payment-submissions`, {
        amountPaise, paidOn, method, reference: reference.trim(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        proof: { fileName: file.name, mimeType: file.type, content: await toBase64(file) },
      }, idempotencyKey.current);
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { inFlight.current = false; setBusy(false); }
  }

  const show = (message: string | null, touched: boolean) => (tried || touched ? message : null);

  return (
    <Dialog
      title={`Submit payment — ${invoice.invoiceNumber}`}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy} disabled={!!duplicateSubmission}>
            {flagged && !duplicateSubmission ? 'Submit anyway' : 'Submit for verification'}
          </Button>
        </>
      )}
    >
      <div className="rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small text-neutral-700">
        <div className="flex justify-between gap-3"><span>Outstanding</span><span className="tabular-nums">{money(invoice.outstandingPaise ?? 0)}</span></div>
        {!!invoice.pendingSubmittedPaise && (
          <div className="flex justify-between gap-3"><span>Already submitted, awaiting verification</span><span className="tabular-nums">{money(invoice.pendingSubmittedPaise)}</span></div>
        )}
        <div className="flex justify-between gap-3 font-medium text-neutral-900"><span>Can be submitted now</span><span className="tabular-nums">{money(available)}</span></div>
      </div>
      <p className="mt-2 text-small text-neutral-600">
        Submitting does not mark the invoice paid. {BRAND.name} checks the payment against its bank statement;
        the invoice counts as paid only once it is confirmed.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <TextField label="Amount (₹)" value={rupees} onChange={setRupees} inputMode="decimal" error={show(amountError, rupees !== (available / 100).toFixed(2))} required />
        <Field label="Payment date" error={show(dateError, false)} required>
          {(p) => <Input {...p} type="date" value={paidOn} max={today} onChange={(e) => setPaidOn(e.target.value)} required />}
        </Field>
        <Field label="Method" required>
          {(p) => (
            <Select {...p} value={method} onChange={(e) => setMethod(e.target.value as typeof method)} className="w-full">
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="UPI">UPI</option>
              <option value="CHEQUE">Cheque</option>
            </Select>
          )}
        </Field>
        <Field label={method === 'CHEQUE' ? 'Cheque number' : 'UTR / reference'} error={show(referenceError, false)} required>
          {(p) => (
            <Input
              {...p} value={reference} required spellCheck={false} autoCapitalize="characters" className="font-mono"
              onChange={(e) => setReference(e.target.value)}
              onBlur={() => { void refCheck.run(reference); }}
            />
          )}
        </Field>
        {seen && (duplicateSubmission ? (
          <Alert tone="danger" className="sm:col-span-2" title={<>Reference <span className="font-mono">{reference.trim()}</span> is already on a submission</>}>
            <ReferenceUseList uses={[duplicateSubmission]} currency={invoice.currency} />
            <p className="mt-1">
              A reference can be on one submission at a time while it is awaiting verification or confirmed. Check the UTR.
              If this one transfer paid several invoices, tell {BRAND.name} finance — they record it against each invoice.
            </p>
          </Alert>
        ) : (
          <ReferenceReuseAlert
            className="sm:col-span-2" reference={reference.trim()} uses={seen.uses} elsewhere={seen.elsewhere}
            currency={invoice.currency} amountPaise={amountError ? undefined : amountPaise}
          >
            If one bank transfer paid several invoices, that is fine — submit, and {BRAND.name} will check the amounts
            against the bank credit. If not, check the UTR before submitting: a wrong reference is rejected.
          </ReferenceReuseAlert>
        ))}
        <Field
          label="Proof of payment"
          hint="Bank advice, UPI receipt or cheque scan — PDF, JPEG or PNG, up to 1 MB."
          error={show(fileError, !!file)}
          required
          className="sm:col-span-2"
        >
          {({ invalid: _invalid, ...p }) => (
            <input
              {...p}
              type="file" accept="application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-small text-neutral-700 file:mr-3 file:h-9 file:cursor-pointer file:rounded-btn file:border file:border-solid file:border-neutral-300 file:bg-white file:px-3 file:text-small file:font-semibold file:text-neutral-800 hover:file:bg-neutral-50"
            />
          )}
        </Field>
        <div className="sm:col-span-2">
          <TextField label="Notes (optional)" value={notes} onChange={setNotes} />
        </div>
      </div>
      {error && <Alert tone="danger" className="mt-3">{error}</Alert>}
    </Dialog>
  );
}
